import React from 'react';

interface EmploymentDetailTabProps {
  employeeData: any;
  getDepartmentName: (id: number | null | undefined) => string;
  getPositionName: (id: number | null | undefined) => string;
  getEmployeeName: (id: number | null | undefined) => string;
}

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

const formatEmploymentStatus = (status: string | null | undefined) => {
  if (!status) return '-';
  const statusMap: Record<string, string> = {
    permanent: 'Tetap',
    contract: 'Kontrak',
    probation: 'Percobaan',
    intern: 'Magang',
  };
  return statusMap[status] || status;
};

const formatStatus = (status: string | null | undefined) => {
  if (!status) return '-';
  const statusMap: Record<string, string> = {
    active: 'Aktif',
    inactive: 'Tidak Aktif',
    terminated: 'Berhenti',
  };
  return statusMap[status] || status;
};

const formatCurrency = (value: number | string | null | undefined) => {
  if (!value) return '-';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '-';
  return `Rp ${new Intl.NumberFormat('id-ID').format(numValue)}`;
};

export default function EmploymentDetailTab({
  employeeData,
  getDepartmentName,
  getPositionName,
  getEmployeeName,
}: EmploymentDetailTabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Data Kepegawaian</h3>
        <p className="text-gray-500 text-sm">Informasi jabatan dan unit kerja</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">NIP</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900 font-mono">
            {employeeData.employee_id || '-'}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tanggal Mulai Bergabung
          </label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {formatDate(employeeData.join_date)}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tanggal Awal Kontrak
          </label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {formatDate(employeeData.contract_start_date)}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Divisi</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {getDepartmentName(employeeData.department_id)}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Jabatan</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {getPositionName(employeeData.position_id)}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Atasan Langsung</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {getEmployeeName(employeeData.manager_id)}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Lokasi Kerja</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {employeeData.work_location || '-'}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status Kepegawaian</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {formatEmploymentStatus(employeeData.employment_status)}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tanggal Berakhir Kontrak
          </label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {formatDate(employeeData.end_date)}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {formatStatus(employeeData.status)}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Level Kompetensi</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {employeeData.competency_level
              ? `${employeeData.competency_level} - ${
                  employeeData.competency_level === 1
                    ? 'Basic'
                    : employeeData.competency_level === 2
                      ? 'Developing'
                      : employeeData.competency_level === 3
                        ? 'Competent'
                        : employeeData.competency_level === 4
                          ? 'Proficient'
                          : 'Expert'
                }`
              : '-'}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Gaji Pokok</label>
          <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md text-emerald-900 font-semibold">
            {formatCurrency(employeeData.base_salary)}
          </div>
        </div>
      </div>

      <div className="pt-4 border-t">
        <h4 className="text-md font-semibold mb-4">Data Keuangan</h4>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nama Bank</label>
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
              {employeeData.bank_name || '-'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">No. Rekening</label>
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900 font-mono">
              {employeeData.bank_account || '-'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">NPWP</label>
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900 font-mono">
              {employeeData.npwp || '-'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">BPJS Kesehatan</label>
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
              {employeeData.bpjs_kesehatan || '-'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              BPJS Ketenagakerjaan
            </label>
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
              {employeeData.bpjs_ketenagakerjaan || '-'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Batas Persetujuan Kredit
            </label>
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
              {formatCurrency(employeeData.credit_approval_limit)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
